/**
 * useTaskEditData - Hook para carregar dados do modal de edição de tarefa
 * 
 * Usa RPC get_task_edit_data que retorna em uma única chamada server-side:
 * - Dados editáveis da tarefa
 * - Categorias ativas do tenant
 * - Fases da categoria da tarefa
 * - Classificações ativas da categoria
 * 
 * @version 1.1.0
 * RELEASE: 2026-08-08 v1.1.0 - Campos vehicle_id/vehicle_label
 * RELEASE: 2026-04-07 v1.0.0 - Criação inicial
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('useTaskEditData');

/**
 * Tipo dos dados editáveis da tarefa retornados pela RPC
 */
export interface TaskEditFields {
  id: string;
  number: number;
  title: string;
  description: string | null;
  description_html: string | null;
  category_id: string;
  phase_id: string;
  classification_id: string | null;
  priority: string;
  assigned_to: string | null;
  due_date: string | null;
  contact_id: string | null;
  deal_id: string | null;
  /** v1.1.0: veículo vinculado (customer_vehicles) */
  vehicle_id: string | null;
  /** v1.1.0: rótulo pronto do veículo (placa - marca modelo) */
  vehicle_label: string | null;
  parent_task_id: string | null;
  /** Prazo da tarefa pai (para validação de subtarefa) */
  parent_due_date: string | null;
}

/**
 * Item de lista auxiliar (categoria, fase, classificação)
 */
export interface TaskEditListItem {
  id: string;
  name: string;
  color: string;
}

/**
 * Item de classificação com hierarquia
 */
export interface TaskEditClassification extends TaskEditListItem {
  parent_id: string | null;
  parent_name: string | null;
}

/**
 * Estrutura completa retornada pela RPC
 */
export interface TaskEditData {
  task: TaskEditFields;
  categories: TaskEditListItem[];
  phases: TaskEditListItem[];
  classifications: TaskEditClassification[];
}

/**
 * Hook para carregar dados do modal de edição de tarefa via RPC server-side
 */
export function useTaskEditData(taskId: string | null) {
  const { currentTenant } = useAuth();

  const query = useQuery({
    queryKey: ['task-edit-data', taskId],
    queryFn: async () => {
      logger.debug('Buscando dados de edição da tarefa', { taskId });

      const { data, error } = await supabase.rpc('get_task_edit_data', {
        p_task_id: taskId!,
        p_tenant_hint: currentTenant?.id || null,
      });

      if (error) {
        logger.error('Erro ao buscar dados de edição', { error: error.message });
        throw error;
      }

      if (!data) {
        throw new Error('Dados de edição não retornados');
      }

      // A RPC retorna JSONB — parse se necessário
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return parsed as TaskEditData;
    },
    enabled: !!taskId && !!currentTenant?.id,
    // Não refetch automático — dados de edição são pontuais
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data || null,
    task: query.data?.task || null,
    categories: query.data?.categories || [],
    phases: query.data?.phases || [],
    classifications: query.data?.classifications || [],
    isLoading: query.isLoading,
    error: query.error,
  };
}
